/**
 * Sensor filtering and smoothing utilities for mobile compass/accelerometer.
 *
 * Provides:
 *   - Low-pass filtering (alpha = 0.85 for stability)
 *   - Median filtering (odd window to reduce outliers)
 *   - Kalman filtering (state-space estimation for compass heading)
 *   - Heading normalization (handles 359°↔0° wrap)
 *   - Jitter detection (identifies unreliable sensors)
 *   - Calibration detection (recognizes when sensor needs recalibration)
 */

/**
 * Low-pass filter on an angle (°) with proper wrap-around handling.
 * Smooths rapid changes while preserving directional accuracy.
 *
 * @param {number|null} prev - previous heading (°)
 * @param {number} next - new heading (°)
 * @param {number} alpha - smoothing factor (0.0 = no smoothing, 1.0 = max smoothing)
 *                         recommended: 0.85 for premium feel
 * @returns {number} smoothed heading (°, 0–360)
 */
export function lowPassFilterHeading(prev, next, alpha = 0.85) {
  if (prev == null || Number.isNaN(prev) || Number.isNaN(next)) return next;
  // Project to unit circle, lerp, project back.
  // This avoids the 359°→0° discontinuity.
  const a = (prev * Math.PI) / 180;
  const b = (next * Math.PI) / 180;
  const x = Math.cos(a) * alpha + Math.cos(b) * (1 - alpha);
  const y = Math.sin(a) * alpha + Math.sin(b) * (1 - alpha);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Median filter for headings using a circular buffer.
 * Reduces outliers from sensor spikes.
 *
 * @param {number[]} headings - array of headings (°)
 * @returns {number} median heading (°)
 */
export function medianHeading(headings) {
  if (!headings || headings.length === 0) return 0;
  if (headings.length === 1) return headings[0];
  // For circular median, convert to unit circle, find mean, convert back.
  let sumCos = 0;
  let sumSin = 0;
  for (const h of headings) {
    const rad = (h * Math.PI) / 180;
    sumCos += Math.cos(rad);
    sumSin += Math.sin(rad);
  }
  return (Math.atan2(sumSin / headings.length, sumCos / headings.length) * 180) / Math.PI + 360 % 360;
}

/**
 * Simple Kalman filter state for heading estimation.
 * Tracks (heading, velocity) to predict and correct heading measurements.
 *
 * Usage:
 *   const filter = new HeadingKalmanFilter();
 *   const smoothed = filter.update(rawHeading, dt);
 */
export class HeadingKalmanFilter {
  constructor({ processNoise = 0.01, measurementNoise = 4.0 } = {}) {
    this.x = 0; // state: [heading, velocity]
    this.v = 0;
    this.P = 1000; // estimation error covariance
    this.Q = processNoise; // process noise
    this.R = measurementNoise; // measurement noise
    this.lastTime = Date.now();
  }

  update(measurement, dt = null) {
    if (dt == null) {
      const now = Date.now();
      dt = (now - this.lastTime) / 1000;
      this.lastTime = now;
    }

    // Normalize measurement
    measurement = (measurement % 360 + 360) % 360;

    // Predict step
    this.x = (this.x + this.v * dt) % 360;
    this.P += this.Q;

    // Correct step (simplified for circular data)
    const delta = ((measurement - this.x + 540) % 360) - 180; // shortest path
    const K = this.P / (this.P + this.R);
    this.x = (this.x + K * delta + 360) % 360;
    this.P = (1 - K) * this.P;

    // Update velocity estimate
    this.v = (delta / Math.max(dt, 0.001)) * K;

    return this.x;
  }

  reset() {
    this.x = 0;
    this.v = 0;
    this.P = 1000;
    this.lastTime = Date.now();
  }
}

/**
 * Detect if compass needs recalibration based on jitter patterns.
 * High variance + stuck readings = uncalibrated sensor.
 */
export class CalibrationDetector {
  constructor(windowSize = 15, jitterThreshold = 12) {
    this.window = [];
    this.windowSize = windowSize;
    this.jitterThreshold = jitterThreshold;
  }

  /**
   * Add a reading and check calibration quality.
   * @returns {object} { needsCalibration: bool, variance: number, status: string }
   */
  addReading(heading) {
    this.window.push((heading % 360 + 360) % 360);
    if (this.window.length > this.windowSize) {
      this.window.shift();
    }

    if (this.window.length < 5) {
      return { needsCalibration: false, variance: 0, status: 'initializing' };
    }

    // Compute circular variance
    let sumCos = 0;
    let sumSin = 0;
    for (const h of this.window) {
      const rad = (h * Math.PI) / 180;
      sumCos += Math.cos(rad);
      sumSin += Math.sin(rad);
    }
    const R = Math.sqrt(sumCos ** 2 + sumSin ** 2) / this.window.length;
    const variance = -2 * Math.log(Math.max(R, 0.001)); // circular variance

    // Detect stuck readings (all same or very close)
    const unique = new Set(this.window.map((h) => Math.round(h / 3) * 3)); // bucket by 3°
    const stuckRatio = unique.size / this.window.length;

    // More permissive thresholds for faster recovery
    const needsCalibration = variance > this.jitterThreshold || stuckRatio < 0.4;
    const status = needsCalibration
      ? stuckRatio < 0.5
        ? 'stuck'
        : 'noisy'
      : 'good';

    return { needsCalibration, variance, status, stuckRatio };
  }

  reset() {
    this.window = [];
  }
}

/**
 * Normalize a bearing to [0, 360) range.
 */
export function normalizeBearing(bearing) {
  return ((bearing % 360) + 360) % 360;
}

/**
 * Calculate signed delta from current heading to target bearing.
 * Returns shortest arc: [-180, 180].
 */
export function bearingDelta(currentHeading, targetBearing) {
  const h = normalizeBearing(currentHeading);
  const b = normalizeBearing(targetBearing);
  return ((b - h + 540) % 360) - 180;
}

/**
 * Apply magnetic declination correction (optional).
 * @param {number} heading - device heading (°)
 * @param {number} declination - magnetic declination (°, negative = west)
 * @returns {number} corrected heading (°)
 */
export function applyMagneticDeclination(heading, declination) {
  return normalizeBearing(heading + declination);
}

/**
 * Detect abrupt heading jumps (sensor fault).
 * Returns true if delta > threshold in short time.
 */
export function detectHeadingJump(prev, next, threshold = 45) {
  if (prev == null) return false;
  const delta = Math.abs(bearingDelta(prev, next));
  return delta > threshold;
}
